import { STRATEGY_IDS } from '../constants/strategyCatalog';

const hasAtsSignal = (job = {}, signal = {}) => {
    const source = (
        signal?.source ||
        signal?.dataSource ||
        signal?.jobSource ||
        job?.source ||
        job?.jobSource ||
        ''
    ).toString().toLowerCase();

    return source.includes('ats');
};

const getJobAgeHours = (job = {}) => {
    const rawDate = job?.datentime || job?.postedAt || job?.createdAt;
    if (!rawDate) return 48;

    const posted = new Date(rawDate);
    if (Number.isNaN(posted.getTime())) return 48;

    const diffMs = Date.now() - posted.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const safeArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [];
};

const normalizeScore = (value) => {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) return 0;
    if (numeric <= 1) return clamp(numeric, 0, 1);
    return clamp(numeric / 100, 0, 1);
};

const getUserSignalStrength = (userProfile = {}) => {
    const skillsCount = safeArray(userProfile?.skills).length;
    const experienceCount = safeArray(userProfile?.experience).length;
    const industriesCount = safeArray(userProfile?.preferredIndustry).length;
    const departmentsCount = safeArray(userProfile?.preferredDepartment).length;
    const rolesCount = safeArray(userProfile?.jobTitle).length;
    const hasCareerGoal = Boolean(userProfile?.careerGoal);

    let score = 0;
    if (skillsCount >= 5) score += 0.3;
    else if (skillsCount >= 2) score += 0.18;
    else if (skillsCount >= 1) score += 0.1;

    if (experienceCount >= 2) score += 0.25;
    else if (experienceCount >= 1) score += 0.16;

    if (industriesCount > 0) score += 0.12;
    if (departmentsCount > 0) score += 0.1;
    if (rolesCount > 0) score += 0.13;
    if (hasCareerGoal) score += 0.1;

    return clamp(score, 0, 1);
};

const getJobCompletenessScore = (job = {}) => {
    const hasTitle = Boolean(job?.jobPosition || job?.jobTitle);
    const hasCompany = Boolean(job?.companyName || job?.company?.name);
    const description = (job?.jobDescription || job?.description || job?.jobDesc || '').trim();
    const hasRichDescription = description.length >= 300;
    const hasAnyDescription = description.length > 40;
    const ageHours = getJobAgeHours(job);
    const hasReasonableDate = ageHours <= 24 * 60;

    let score = 0;
    if (hasTitle) score += 0.35;
    if (hasCompany) score += 0.2;
    if (hasRichDescription) score += 0.3;
    else if (hasAnyDescription) score += 0.18;
    if (hasReasonableDate) score += 0.15;

    return clamp(score, 0, 1);
};

const getReason = ({ strategyId, atsFirst, ageHours, hasOpenRole, signalStrength, recruiterCount }) => {
    if (strategyId === STRATEGY_IDS.ATS_FIRST) {
        return atsFirst
            ? 'ATS-sourced opportunity and strong urgency signals favor a speed-first ATS execution plan.'
            : 'Role urgency and recency indicate an ATS-first cadence will protect your first-mover advantage.';
    }

    if (strategyId === STRATEGY_IDS.REFERRAL_NETWORK) {
        if (recruiterCount >= 2 && signalStrength >= 0.7) {
            return 'Strong hiring signal plus reachable contacts make a referral-led path the highest-upside move.';
        }
        if (ageHours <= 48) {
            return 'Fresh role timing and available contact paths make referral momentum the best near-term lever.';
        }
        return 'Signal quality suggests warm internal advocacy should outperform pure outbound application flow.';
    }

    if (strategyId === STRATEGY_IDS.STRATEGIC_OUTREACH) {
        if (hasOpenRole) {
            return 'Open role context and profile fit support targeted outreach to stand out beyond standard applications.';
        }
        return 'Moderate hiring signals favor structured strategic outreach while waiting for stronger role openings.';
    }

    return 'Current data favors steady relationship-building until stronger urgency or referral signals emerge.';
};

export const recommendStrategy = ({ job = {}, signal = null, userProfile = {}, context = {} } = {}) => {
    const ageHours = getJobAgeHours(job);
    const atsFirst = hasAtsSignal(job, signal);
    const hasOpenRole = Boolean(job?.jobPosition || job?.jobTitle || job?.jobId || job?._id);
    const signalStrength = normalizeScore(
        signal?.hiring_signal_score || signal?.overallScore || signal?.signalScore || context?.signalStrength || 0
    );
    const recruiterCount = Number(context?.recruiterCount || 0);
    const userSignalStrength = getUserSignalStrength(userProfile);
    const jobCompleteness = getJobCompletenessScore(job);

    const urgency = clamp(((hasOpenRole ? 0.45 : 0.15) + (ageHours <= 24 ? 0.4 : ageHours <= 72 ? 0.22 : 0.08) + (atsFirst ? 0.25 : 0)), 0, 1);
    const referralReadiness = clamp((signalStrength * 0.45) + (Math.min(recruiterCount, 4) / 4) * 0.35 + (userSignalStrength * 0.2), 0, 1);

    const scores = {
        [STRATEGY_IDS.ATS_FIRST]: clamp((urgency * 0.6) + (signalStrength * 0.2) + (jobCompleteness * 0.2), 0, 1),
        [STRATEGY_IDS.REFERRAL_NETWORK]: clamp((referralReadiness * 0.55) + (urgency * 0.2) + (signalStrength * 0.25), 0, 1),
        [STRATEGY_IDS.STRATEGIC_OUTREACH]: clamp((hasOpenRole ? 0.2 : 0.1) + (userSignalStrength * 0.3) + (jobCompleteness * 0.3) + (signalStrength * 0.2), 0, 1),
        [STRATEGY_IDS.BASELINE_NETWORKING]: clamp(((1 - urgency) * 0.35) + ((1 - signalStrength) * 0.25) + ((1 - referralReadiness) * 0.2) + (userSignalStrength * 0.2), 0, 1)
    };

    if (atsFirst) {
        scores[STRATEGY_IDS.ATS_FIRST] = clamp(scores[STRATEGY_IDS.ATS_FIRST] + 0.25, 0, 1);
    }

    const rankedStrategies = Object.entries(scores)
        .map(([strategyId, score]) => ({ strategyId: Number(strategyId), score }))
        .sort((a, b) => b.score - a.score);

    const top = rankedStrategies[0];
    const second = rankedStrategies[1] || { score: 0 };
    const margin = clamp(top.score - second.score, 0, 1);
    const evidenceStrength = clamp((userSignalStrength * 0.35) + (jobCompleteness * 0.35) + (signalStrength * 0.3), 0, 1);

    const confidence = Math.round(clamp(58 + (top.score * 24) + (margin * 18) + (evidenceStrength * 6), 58, 97));

    return {
        strategyId: top.strategyId,
        confidence,
        reason: getReason({
            strategyId: top.strategyId,
            atsFirst,
            ageHours,
            hasOpenRole,
            signalStrength,
            recruiterCount
        })
    };
};
